/*
 * Copyright (c) 2025 Sila Artificial Intelligence Research LLC
 * A DA Holding Ltd Company
 * All rights reserved.
 *
 * PROPRIETARY AND CONFIDENTIAL
 * This software and its documentation are proprietary to Sila Artificial
 * Intelligence Research LLC. Unauthorized copying, distribution, or use
 * of this software is strictly prohibited.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL
 * THE AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING
 * FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER
 * DEALINGS IN THE SOFTWARE.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { Bar, Line, Radar, Doughnut } from 'react-chartjs-2';
import { useTheme } from '../../contexts/ThemeContext';
import { isDemoMode } from '../../services';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  RadialLinearScale,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { getChartLabelPlugin } from 'chart.js-plugin-labels-dv';
import { Metrics, Metric, CrossTabsChart } from '../../config/api';
import { useFilters } from '../../contexts/FiltersContext';
import useBrandMetrics from '../../hooks/useBrandMetrics';
import { usePageInsights } from '../../hooks/usePageInsights';
import AIInsightPanel from '../../components/AIInsightPanel';
import { Navigate, NavLink, useParams } from 'react-router-dom';
import ActiveFilters from '../../components/ActiveFilters';
import DataTable, { Align, Column } from '../../components/DataTable';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  RadialLinearScale,
  Title,
  Tooltip,
  Legend,
  Filler,
  ChartDataLabels,
  getChartLabelPlugin()
);

/**
 * Deep Dive Dashboard - Comprehensive brand health analysis with demographic filtering
 * Replaces traditional static PowerPoint reports with interactive data exploration
 */
const POSSIBLE_TABS = ['awareness' , 'funnel' , 'attributes' , 'media' , 'crosstab']
const DeepDiveDashboard: React.FC = () => {
  const { isDark } = useTheme();
  const {tab} = useParams<{tab: string}>()
  const { selectedBrand, selectedFilters } = useFilters()
  const { health, loading } = useBrandMetrics()
  const pageNameMap = {
    awareness: 'deepdive-awareness',
    funnel: 'deepdive-funnel',
    attributes: 'deepdive-attributes',
    media: 'deepdive-media',
    crosstab: 'deepdive-crosstab',
  } as const;
  const pageName = tab && tab in pageNameMap ? pageNameMap[tab as keyof typeof pageNameMap] : undefined;
  const { insights: pageInsights, loading: pageInsightsLoading } = usePageInsights(pageName!);
  // const [activeView, setActiveView] = useState<'awareness' | 'funnel' | 'attributes' | 'media' | 'crosstab'>('awareness');

  // All available metrics from brand health study
  const allMetrics: Record<'funnel'|'attributes', (keyof Metrics)[]> = {
    funnel: ['awareness', 'consideration', 'usage', 'recent_usage', 'primary_brand', 'loyalty'],
    attributes: ['trust', 'pride', 'innovation', 'value', 'quality', 'taste', 'texture', 'natural'],
    // product: ['free_of_broken_rice', 'grain_length', 'grains_separated', 'convenient_pack_sizes'],
    // availability: ['easily_available', 'versatile_usage', 'nutritious', 'quantity_after_cooking']
  };

  // Attribute performance data
  const attributeData = useMemo(() => ({
    labels: ['Trust', 'Pride', 'Innovation', 'Value', 'Quality', 'Taste', 'Texture', 'Natural'],
    datasets: [{
      label: 'Performance',
      data: [
        health?.traditional.metrics?.trust || 0,
        health?.traditional.metrics?.pride || 0,
        health?.traditional.metrics?.innovation || 0,
        health?.traditional.metrics?.value || 0,
        health?.traditional.metrics?.quality || 0,
        health?.traditional.metrics?.taste || 0,
        health?.traditional.metrics?.texture || 0,
        health?.traditional.metrics?.natural || 0
      ],
      backgroundColor: 'rgba(16, 185, 129, 0.2)',
      borderColor: 'rgba(16, 185, 129, 1)',
      pointBackgroundColor: 'rgba(16, 185, 129, 1)',
      pointBorderColor: '#fff',
      pointHoverBackgroundColor: '#fff',
      pointHoverBorderColor: 'rgba(16, 185, 129, 1)'
    }]
  }), [health]);

  type Questions = keyof CrossTabsChart
  type Demographics = keyof CrossTabsChart[Questions]

  const [selectedDemographic, setSelectedDemographics] = useState<Demographics>('age')
  const possibleDemographics = useMemo<Record<Demographics, string[]>>(() => {
    const items = Object.values(health?.traditional.traditional_charts.cross_tabs || {})

    if (items.length === 0) return {} as Record<Demographics, string[]>

    return Object.entries(items[0]).filter(([key, _]) => key !== 'overall').reduce((acc, [key, val]) => ({...acc, [key as Demographics]: val.map(v => v.label)}), {} as Record<Demographics, string[]>)
  }, [health])
  const crossTabColumns = useMemo<Column<string>[]>(() => {
    const defaultColumns = [
      {key: 'metric', title: 'Metric'},
      {key: 'overall', title: 'Overall', align: 'center' as Align},
    ]

    if (!possibleDemographics[selectedDemographic])
      return defaultColumns

    return [
      ...defaultColumns,
      ...possibleDemographics[selectedDemographic].map(pd => ({key: pd, title: pd, align: 'center' as Align})),
    ]
  }, [health, selectedDemographic, possibleDemographics])
  const crossTabData = useMemo(() => {
    if (!selectedDemographic || !Object.keys(possibleDemographics).includes(selectedDemographic))
      return []

    const metricsData: Record<string, string>[] = []

    for (const [metric, demographicsData] of Object.entries(health?.traditional.traditional_charts.cross_tabs || {})) {
      let metricEntry: Record<string, string> = {metric}

      let found = false;
      for (const [demographic, metrics] of Object.entries(demographicsData)) {
        if (demographic === "overall") {
          for (const {label, value} of metrics) {
            metricEntry[label] = value.toFixed(0) + "%"
          }
        }

        if (demographic !== selectedDemographic)
          continue;

        for (const {label, value} of metrics) {
          found = true;
          metricEntry[label] = value.toFixed(0) + "%"
        }
      }

      if (found) {
        metricsData.push(metricEntry)
      }
    }

    return metricsData
  }, [health, selectedDemographic])

  useEffect(() => {
    if (!selectedDemographic && Object.keys(possibleDemographics).length > 0) {
      setSelectedDemographics(Object.keys(possibleDemographics)[0] as Demographics)
    }
  }, [selectedDemographic, possibleDemographics])

  if (!health?.traditional.metrics) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-text-muted">No traditional metrics available</div>
      </div>
    );
  }

  // Funnel visualization data with colors matching Usage Metrics KPI cards
  const funnelData = {
    labels: ['Awareness', 'Consideration', 'Usage', 'Recent Usage', 'MOUB', 'Loyalty'],
    datasets: [{
      label: health?.traditional.name,
      data: [
        health?.traditional.metrics.awareness || 0,
        health?.traditional.metrics.consideration || 0,
        health?.traditional.metrics.usage || 0,
        health?.traditional.metrics.recent_usage || 0,
        health?.traditional.metrics.primary_brand || 0,
        health?.traditional.metrics.loyalty || 0
      ],
      backgroundColor: [
        'rgba(16, 185, 129, 0.8)',   // Green/Emerald - Awareness
        'rgba(147, 51, 234, 0.8)',   // Purple - Consideration
        'rgba(251, 146, 60, 0.8)',   // Orange - Usage
        'rgba(244, 63, 94, 0.8)',    // Rose - Recent Usage
        'rgba(59, 130, 246, 0.8)',   // Blue - MOUB
        'rgba(236, 72, 153, 0.8)',   // Pink - Loyalty
      ],
      borderColor: [
        'rgba(16, 185, 129, 1)',
        'rgba(147, 51, 234, 1)',
        'rgba(251, 146, 60, 1)',
        'rgba(244, 63, 94, 1)', 
        'rgba(59, 130, 246, 1)',
        'rgba(236, 72, 153, 1)',
      ],
      borderWidth: 2
    }]
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        labels: {
          color: isDark ? '#E5E7EB' : '#374151'
        }
      },
      datalabels: {
        color: isDark ? '#E5E7EB' : '#374151',
        font: {
          weight: 'bold' as const,
          size: 12
        },
        formatter: (value: number) => `${value.toFixed(0)}%`,
        anchor: 'end' as const,
        align: 'top' as const
      }
    },
    scales: {
      x: {
        grid: {
          color: isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.08)'
        },
        ticks: {
          color: isDark ? '#9CA3AF' : '#4B5563'
        }
      },
      y: {
        grid: {
          color: isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.08)'
        },
        ticks: {
          color: isDark ? '#9CA3AF' : '#4B5563',
          callback: (value: any) => `${value}%`
        }
      }
    }
  };

  // Options for Doughnut chart (media)
  const doughnutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    layout: {
      padding: {
        top: 40,
        bottom: 20,
        left: 60,
        right: 60
      }
    },
    plugins: {
      legend: {
        position: 'bottom' as const,
        labels: {
          color: isDark ? '#E5E7EB' : '#374151',
          padding: 15
        }
      },
      datalabels: {
        display: false
      },
      labels: {
        render: (args: any) => `${args.label}: ${args.value.toFixed(0)}%`,
        position: 'outside' as const,
        textMargin: 8,
        outsidePadding: 4,
        fontColor: isDark ? '#E5E7EB' : '#374151',
        fontSize: 11,
        fontStyle: 'bold'
      }
    }
  };

  const radarOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        labels: {
          color: isDark ? '#E5E7EB' : '#374151'
        }
      },
      datalabels: {
        color: isDark ? '#E5E7EB' : '#374151',
        font: {
          weight: 'bold' as const,
          size: 10
        },
        formatter: (value: number) => `${value.toFixed(0)}%`,
        anchor: 'end' as const,
        align: 'end' as const,
        offset: 4
      }
    },
    scales: {
      r: {
        grid: {
          color: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
        },
        pointLabels: {
          color: isDark ? '#E5E7EB' : '#1F2937',
          font: {
            size: 11
          }
        },
        ticks: {
          display: false
        },
        suggestedMin: 0,
        suggestedMax: 100
      }
    }
  };

  return (
    <div className="space-y-6">
      {/* Header with Filters */}
      <div className="bg-card rounded-2xl p-6 border border-border">
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-foreground">Brand Deep Dive Analysis</h2>
          <p className="text-text-muted mt-1">Comprehensive brand health exploration with demographic cuts</p>
        </div>

        {/* View Selector with Export */}
        <div className="flex items-center space-x-3">
          <div className="flex space-x-1 bg-background-secondary rounded-xl p-1 border border-border">
            <NavLink to="/dashboard/deepdive/awareness" className={({isActive}) => `px-4 py-2.5 rounded-lg font-medium text-sm transition-all ${
                isActive
                  ? 'bg-primary text-primary-foreground shadow-sm'
                  : 'text-text-muted hover:text-foreground hover:bg-background-tertiary'
              }`}>
              <i className="fas fa-eye mr-2"></i>
              Awareness
            </NavLink>
            <NavLink
              to='/dashboard/deepdive/funnel'
              className={({isActive}) => `px-4 py-2.5 rounded-lg font-medium text-sm transition-all ${
                isActive
                  ? 'bg-primary text-primary-foreground shadow-sm'
                  : 'text-text-muted hover:text-foreground hover:bg-background-tertiary'
              }`}
            >
              <i className="fas fa-filter mr-2"></i>
              Funnel
            </NavLink>
            <NavLink
              to='/dashboard/deepdive/attributes'
              className={({isActive}) => `px-4 py-2.5 rounded-lg font-medium text-sm transition-all ${
                isActive
                  ? 'bg-primary text-primary-foreground shadow-sm'
                  : 'text-text-muted hover:text-foreground hover:bg-background-tertiary'
              }`}
            >
              <i className="fas fa-star mr-2"></i>
              Attributes
            </NavLink>
            <NavLink
              to='/dashboard/deepdive/media'
              className={({isActive}) => `px-4 py-2.5 rounded-lg font-medium text-sm transition-all ${
                isActive
                  ? 'bg-primary text-primary-foreground shadow-sm'
                  : 'text-text-muted hover:text-foreground hover:bg-background-tertiary'
              }`}
            >
              <i className="fas fa-bullhorn mr-2"></i>
              Media
            </NavLink>
            <NavLink
              to='/dashboard/deepdive/crosstab'
              className={({isActive}) => `px-4 py-2.5 rounded-lg font-medium text-sm transition-all ${
                isActive
                  ? 'bg-primary text-primary-foreground shadow-sm'
                  : 'text-text-muted hover:text-foreground hover:bg-background-tertiary'
              }`}
            >
              <i className="fas fa-table mr-2"></i>
              Cross-Tabs
            </NavLink>
          </div>
        </div>
      </div>

      {/* Active Filters Display */}
      <ActiveFilters selectedFilters={selectedFilters} />

      {/* Main Content Area */}
      <div id="deepdive-dashboard" className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Visualization */}
        <div className="lg:col-span-2">
          <div className="bg-card rounded-2xl p-6 border border-border">
            {loading? (
              <div className="flex items-center justify-center h-80">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
              </div>
            ): tab === 'awareness'? (
              <div className="relative">
                {/* New Banner */}
                {isDemoMode && (<div
                  className="absolute -top-6 -right-6 text-white text-sm font-bold px-4 py-1.5 rounded-bl-xl shadow-lg z-10"
                  style={{
                    animation: 'flashColors 1s ease-in-out infinite',
                    background: 'linear-gradient(90deg, #10b981, #facc15)'
                  }}
                >
                  ✨ NEW
                </div>)}
                <h3 className="text-xl font-semibold text-foreground mb-4">Awareness Analysis</h3>
                <p className="text-text-muted text-sm mb-6">Understanding brand visibility across different awareness levels</p>

                {/* Awareness Levels Funnel */}
                <div className="grid grid-cols-4 gap-4 mb-8">
                  {/* Top of Mind */}
                  <div className="bg-gradient-to-br from-purple-500/20 to-purple-600/10 rounded-xl p-4 border border-purple-500/30 text-center">
                    <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-purple-500/20 flex items-center justify-center">
                      <i className="fas fa-brain text-purple-500 text-xl"></i>
                    </div>
                    <p className="text-xs text-text-muted mb-1">Top of Mind</p>
                    <p className="text-3xl font-bold text-purple-500">{(health?.traditional.metrics.total_top_of_mind || 0).toFixed(0)}%</p>
                    <p className="text-xs text-text-muted mt-1">First brand recalled</p>
                  </div>

                  {/* Spontaneous */}
                  <div className="bg-gradient-to-br from-blue-500/20 to-blue-600/10 rounded-xl p-4 border border-blue-500/30 text-center">
                    <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-blue-500/20 flex items-center justify-center">
                      <i className="fas fa-lightbulb text-blue-500 text-xl"></i>
                    </div>
                    <p className="text-xs text-text-muted mb-1">Spontaneous</p>
                    <p className="text-3xl font-bold text-blue-500">{(health?.traditional.metrics.total_spontaneous || 0).toFixed(0)}%</p>
                    <p className="text-xs text-text-muted mt-1">Unaided recall</p>
                  </div>

                  {/* Aided */}
                  <div className="bg-gradient-to-br from-cyan-500/20 to-cyan-600/10 rounded-xl p-4 border border-cyan-500/30 text-center">
                    <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-cyan-500/20 flex items-center justify-center">
                      <i className="fas fa-hand-pointer text-cyan-500 text-xl"></i>
                    </div>
                    <p className="text-xs text-text-muted mb-1">Aided</p>
                    <p className="text-3xl font-bold text-cyan-500">{(health?.traditional.metrics.total_aided || 0).toFixed(0)}%</p>
                    <p className="text-xs text-text-muted mt-1">Prompted recognition</p>
                  </div>

                  {/* Total Awareness */}
                  <div className="bg-gradient-to-br from-green-500/20 to-green-600/10 rounded-xl p-4 border border-green-500/30 text-center">
                    <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-green-500/20 flex items-center justify-center">
                      <i className="fas fa-chart-pie text-green-500 text-xl"></i>
                    </div>
                    <p className="text-xs text-text-muted mb-1">Total Awareness</p>
                    <p className="text-3xl font-bold text-green-500">{(health?.traditional.metrics.total_awareness || 0).toFixed(0)}%</p>
                    <p className="text-xs text-text-muted mt-1">Overall awareness</p>
                  </div>
                </div>

                {/* Awareness Trend Chart */}
                <div className="mb-8 relative">
                  {isDemoMode && (<div
                    className="absolute -top-2 right-0 text-white text-xs font-bold px-3 py-1 rounded-lg shadow-lg z-10"
                    style={{
                      animation: 'flashColors 1s ease-in-out infinite',
                      background: 'linear-gradient(90deg, #10b981, #facc15)'
                    }}
                  >
                    ✨ NEW
                  </div>)}
                  <h4 className="text-lg font-medium text-foreground mb-4">Awareness Trend Over Time</h4>
                  <div className="h-64" id="deepdive-awareness-chart">
                    <Line
                      data={health?.traditional.traditional_charts.awareness_over_time || {labels: [], datasets: []}/* {
                        labels: ['Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
                        datasets: [
                          {
                            label: 'Top of Mind',
                            data: [10, 11, 12, 11, 13, 12, ((metrics?.metrics.awareness || 70) * 0.15)],
                            borderColor: 'rgb(168, 85, 247)',
                            backgroundColor: 'rgba(168, 85, 247, 0.1)',
                            tension: 0.4,
                          },
                          {
                            label: 'Spontaneous',
                            data: [28, 30, 32, 31, 33, 32, ((metrics?.metrics.awareness || 70) * 0.45)],
                            borderColor: 'rgb(59, 130, 246)',
                            backgroundColor: 'rgba(59, 130, 246, 0.1)',
                            tension: 0.4,
                          },
                          {
                            label: 'Aided',
                            data: [55, 58, 60, 59, 62, 61, ((metrics?.metrics.awareness || 70) * 0.85)],
                            borderColor: 'rgb(6, 182, 212)',
                            backgroundColor: 'rgba(6, 182, 212, 0.1)',
                            tension: 0.4,
                          },
                          {
                            label: 'Total',
                            data: [65, 68, 72, 70, 74, 73, (metrics?.metrics.awareness || 70)],
                            borderColor: 'rgb(34, 197, 94)',
                            backgroundColor: 'rgba(34, 197, 94, 0.1)',
                            tension: 0.4,
                            borderWidth: 3,
                          }
                        ]
                      } */}
                      options={{
                        ...chartOptions,
                        plugins: {
                          ...chartOptions.plugins,
                          legend: {
                            position: 'top' as const,
                            labels: {
                              color: isDark ? '#E5E7EB' : '#374151',
                              usePointStyle: true,
                            }
                          },
                          datalabels: {
                            display: false
                          }
                        }
                      }}
                    />
                  </div>
                </div>


                {/* Awareness by Demographics */}
                <div className="relative">
                  {isDemoMode && (<div
                    className="absolute -top-2 right-0 text-white text-xs font-bold px-3 py-1 rounded-lg shadow-lg z-10"
                    style={{
                      animation: 'flashColors 1s ease-in-out infinite',
                      background: 'linear-gradient(90deg, #10b981, #facc15)'
                    }}
                  >
                    ✨ NEW
                  </div>)}
                  <h4 className="text-lg font-medium text-foreground mb-4">Awareness by Demographics</h4>
                  <div className="grid grid-cols-2 gap-6">
                    {/* By Age Group */}
                    {health?.traditional.traditional_charts.demographics_metrics['age'] && (
                    <div className="bg-background-secondary rounded-xl p-4 border border-border">
                      <h5 className="text-sm font-medium text-foreground mb-3">By Age Group</h5>
                      <div className="space-y-3">
                        {/* [
                          { label: '18-24', value: ((metrics?.metrics.awareness || 70) * 0.85).toFixed(0) },
                          { label: '25-34', value: ((metrics?.metrics.awareness || 70) * 0.95).toFixed(0) },
                          { label: '35-44', value: ((metrics?.metrics.awareness || 70) * 1.05).toFixed(0) },
                          { label: '45+', value: ((metrics?.metrics.awareness || 70) * 1.1).toFixed(0) }
                        ] */
                        health?.traditional.traditional_charts.demographics_metrics['age'].map(item => (
                          <div key={item.label}>
                            <div className="flex justify-between text-sm mb-1">
                              <span className="text-text-muted">{item.label}</span>
                              <span className="text-foreground font-medium">{Number(item.value).toFixed(2)}%</span>
                            </div>
                            <div className="w-full bg-background-tertiary rounded-full h-2">
                              <div
                                className="bg-blue-500 h-2 rounded-full"
                                style={{ width: `${Math.min(100, Number(item.value))}%` }}
                              ></div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>)}

                    {/* By Region */}
                    {health?.traditional.traditional_charts.demographics_metrics['nationality'] && (
                    <div className="bg-background-secondary rounded-xl p-4 border border-border">
                      <h5 className="text-sm font-medium text-foreground mb-3">By Nationality</h5>
                      <div className="space-y-3">
                        {/* [
                          { label: 'Riyadh', value: ((metrics?.metrics.awareness || 70) * 1.15).toFixed(0) },
                          { label: 'Jeddah', value: ((metrics?.metrics.awareness || 70) * 1.08).toFixed(0) },
                          { label: 'Eastern', value: ((metrics?.metrics.awareness || 70) * 0.95).toFixed(0) },
                          { label: 'Other', value: ((metrics?.metrics.awareness || 70) * 0.82).toFixed(0) }
                        ] */
                        health?.traditional.traditional_charts.demographics_metrics['nationality'].map(item => (
                          <div key={item.label}>
                            <div className="flex justify-between text-sm mb-1">
                              <span className="text-text-muted">{item.label}</span>
                              <span className="text-foreground font-medium">{Number(item.value).toFixed(2)}%</span>
                            </div>
                            <div className="w-full bg-background-tertiary rounded-full h-2">
                              <div
                                className="bg-green-500 h-2 rounded-full"
                                style={{ width: `${Math.min(100, Number(item.value))}%` }}
                              ></div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>)}
                  </div>
                </div>
              </div>
            ): tab === 'funnel'? (
              <>
                {/* Awareness to Usage Flow */}
                <div className="mb-8 bg-background-secondary rounded-xl p-4 border border-border">
                  <h4 className="text-sm font-medium text-foreground mb-4">Awareness to Usage Conversion</h4>
                  <div className="flex items-center justify-between">
                    <div className="text-center flex-1">
                      <p className="text-2xl font-bold text-green-500">{(health?.traditional.metrics.total_awareness || 0).toFixed(0)}%</p>
                      <p className="text-xs text-text-muted">Total Awareness</p>
                    </div>
                    <i className="fas fa-arrow-right text-text-muted mx-2"></i>
                    <div className="text-center flex-1">
                      <p className="text-2xl font-bold text-amber-500">{(health?.traditional.metrics.total_recent_usage || 0).toFixed(0)}%</p>
                      <p className="text-xs text-text-muted">Ever Used</p>
                    </div>
                    <i className="fas fa-arrow-right text-text-muted mx-2"></i>
                    <div className="text-center flex-1">
                      <p className="text-2xl font-bold text-rose-500">{(health?.traditional.metrics.total_p12m_usage || 0).toFixed(0)}%</p>
                      <p className="text-xs text-text-muted">P12M Usage</p>
                    </div>
                    <i className="fas fa-arrow-right text-text-muted mx-2"></i>
                    <div className="text-center flex-1">
                      <p className="text-2xl font-bold text-indigo-500">{(health?.traditional.metrics.total_moub || 0).toFixed(0)}%</p>
                      <p className="text-xs text-text-muted">MOUB</p>
                    </div>
                  </div>
                  <div className="mt-4 pt-4 border-t border-border">
                    <div className="grid grid-cols-3 gap-4 text-center">
                      <div>
                        <p className="text-lg font-semibold text-foreground">
                          {(health?.traditional.metrics.total_recent_usage / (health?.traditional.metrics.total_awareness || 1) * 100).toFixed(0)}%
                        </p>
                        <p className="text-xs text-text-muted">Awareness → Trial</p>
                      </div>
                      <div>
                        <p className="text-lg font-semibold text-foreground">
                          {((health?.traditional.metrics.total_p12m_usage || 0) / (health?.traditional.metrics.total_recent_usage || 1) * 100).toFixed(0)}%
                        </p>
                        <p className="text-xs text-text-muted">Trial → P12M</p>
                      </div>
                      <div>
                        <p className="text-lg font-semibold text-foreground">
                          {((health?.traditional.metrics.total_moub || 0) / (health?.traditional.metrics.total_p12m_usage || 1) * 100).toFixed(0)}%
                        </p>
                        <p className="text-xs text-text-muted">P12M → MOUB</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Brand Conversion Funnel Chart */}
                <h3 className="text-xl font-semibold text-foreground mb-4">Brand Conversion Funnel</h3>
                <div className="h-80" id="deepdive-funnel-chart">
                  <Bar data={funnelData} options={chartOptions} />
                </div>
                <div className="mt-6 grid grid-cols-3 gap-4">
                  <div className="text-center">
                    <p className="text-text-muted text-sm">Conversion Rate</p>
                    <p className="text-2xl font-bold text-foreground">
                      {health?.traditional.metrics.awareness && health?.traditional.metrics.awareness > 0
                        ? ((health?.traditional.metrics.consideration / health?.traditional.metrics.awareness) * 100).toFixed(0)
                        : '0.0'}%
                    </p>
                    <p className="text-xs text-text-muted">Awareness → Consideration</p>
                  </div>
                  <div className="text-center">
                    <p className="text-text-muted text-sm">Trial Rate</p>
                    <p className="text-2xl font-bold text-foreground">
                      {health?.traditional.metrics.consideration && health?.traditional.metrics.consideration > 0
                        ? ((health?.traditional.metrics.usage / health?.traditional.metrics.consideration) * 100).toFixed(0)
                        : '0.0'}%
                    </p>
                    <p className="text-xs text-text-muted">Consideration → Usage</p>
                  </div>
                  <div className="text-center">
                    <p className="text-text-muted text-sm">Main Brand Conversion</p>
                    <p className="text-2xl font-bold text-foreground">
                      {health?.traditional.metrics.awareness && health?.traditional.metrics.awareness > 0
                        ? ((health?.traditional.metrics.primary_brand / health?.traditional.metrics.awareness) * 100).toFixed(0)
                        : '0.0'}%
                    </p>
                    <p className="text-xs text-text-muted">Awareness → MOUB</p>
                  </div>
                </div>
              </>
            ): tab === 'attributes'? (
              <>
                <h3 className="text-xl font-semibold text-foreground mb-4">Brand Attribute Performance</h3>
                <div className="h-80" id="deepdive-attributes-chart">
                  <Radar data={attributeData} options={radarOptions} />
                </div>
              </>
            ): tab === 'media'? (
              <>
                <h3 className="text-xl font-semibold text-foreground mb-4">Media Touchpoint Effectiveness</h3>
                <div className="h-80" id="deepdive-media-chart">
                  <Doughnut data={health.traditional.traditional_charts.media_reach} options={doughnutOptions} />
                </div>
              </>
            ): tab === 'crosstab'? (
              <div id="deepdive-crosstab-chart">
                <div className="flex items-center mb-4">
                  <div className="flex-1">
                    <h3 className="text-xl font-semibold text-foreground">Cross-Tabulation Matrix</h3>
                    <p className="text-text-muted mt-1">Demographic breakdowns of key metrics</p>
                  </div>
                  <div className="relative">
                    <select
                      value={selectedDemographic}
                      onChange={(e) => setSelectedDemographics(e.target.value as Demographics)}
                      className="appearance-none bg-card border border-[var(--border)] rounded-full px-3 py-1.5 pr-7 text-xs font-medium text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 hover:border-primary/30 transition-all cursor-pointer capitalize"
                    >
                      {Object.keys(possibleDemographics).map(demographic => (
                        <option key={demographic} value={demographic}>{demographic}</option>
                      ))}
                    </select>
                    <i className="fas fa-chevron-down absolute right-2.5 top-1/2 -translate-y-1/2 text-text-muted text-[10px] pointer-events-none"></i>
                  </div>
                </div>
                <DataTable columns={crossTabColumns} data={crossTabData} />
                {/* <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-border">
                        <th className="text-left py-3 px-4 text-text-muted font-medium">Metric</th>
                        <th className="text-center py-3 px-4 text-text-muted font-medium">Overall</th>
                        <th className="text-center py-3 px-4 text-text-muted font-medium">Male</th>
                        <th className="text-center py-3 px-4 text-text-muted font-medium">Female</th>
                        <th className="text-center py-3 px-4 text-text-muted font-medium">18-34</th>
                        <th className="text-center py-3 px-4 text-text-muted font-medium">35+</th>
                      </tr>
                    </thead>
                    <tbody>
                      {allMetrics.funnel.map(metric => (
                        <tr key={metric} className="border-b border-border hover:bg-background-tertiary">
                          <td className="py-3 px-4 text-foreground capitalize">{metric.replace('_', ' ')}</td>
                          <td className="text-center py-3 px-4 text-foreground font-medium">
                            {((metrics.metrics?.[metric] || 0)).toFixed(1)}%
                          </td>
                          <td className="text-center py-3 px-4 text-text-secondary">
                            {Math.min(100, Math.max(0, (metrics?.metrics?.[metric] || 0) - 5)).toFixed(1)}%
                          </td>
                          <td className="text-center py-3 px-4 text-text-secondary">
                            {Math.min(100, Math.max(0, (metrics?.metrics?.[metric] || 0) + 5)).toFixed(1)}%
                          </td>
                          <td className="text-center py-3 px-4 text-text-secondary">
                            {Math.min(100, Math.max(0, (metrics?.metrics?.[metric] || 0) - 10)).toFixed(1)}%
                          </td>
                          <td className="text-center py-3 px-4 text-text-secondary">
                            {Math.min(100, Math.max(0, (metrics?.metrics?.[metric] || 0) + 10)).toFixed(1)}%
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div> */}

                {/* Geographic Breakdown Table */}
                {/* <div className="mt-8">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h3 className="text-xl font-semibold text-foreground">Geographic Breakdown</h3>
                      <p className="text-text-muted mt-1">Regional performance across key metrics</p>
                    </div>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-border">
                          <th className="text-left py-3 px-4 text-text-muted font-medium">Metric</th>
                          <th className="text-center py-3 px-4 text-text-muted font-medium">Overall</th>
                          <th className="text-center py-3 px-4 text-text-muted font-medium">Dubai</th>
                          <th className="text-center py-3 px-4 text-text-muted font-medium">Abu Dhabi</th>
                          <th className="text-center py-3 px-4 text-text-muted font-medium">Sharjah</th>
                          <th className="text-center py-3 px-4 text-text-muted font-medium">Other Emirates</th>
                        </tr>
                      </thead>
                      <tbody>
                        {allMetrics.funnel.map(metric => (
                          <tr key={`geo-${metric}`} className="border-b border-border hover:bg-background-tertiary">
                            <td className="py-3 px-4 text-foreground capitalize">{metric.replace('_', ' ')}</td>
                            <td className="text-center py-3 px-4 text-foreground font-medium">
                              {((metrics?.metrics?.[metric] || 0)).toFixed(1)}%
                            </td>
                            <td className="text-center py-3 px-4 text-text-secondary">
                              {Math.min(100, Math.max(0, (metrics?.metrics?.[metric] || 0) + 8)).toFixed(1)}%
                            </td>
                            <td className="text-center py-3 px-4 text-text-secondary">
                              {Math.min(100, Math.max(0, (metrics?.metrics?.[metric] || 0) + 3)).toFixed(1)}%
                            </td>
                            <td className="text-center py-3 px-4 text-text-secondary">
                              {Math.min(100, Math.max(0, (metrics?.metrics?.[metric] || 0) - 4)).toFixed(1)}%
                            </td>
                            <td className="text-center py-3 px-4 text-text-secondary">
                              {Math.min(100, Math.max(0, (metrics?.metrics?.[metric] || 0) - 7)).toFixed(1)}%
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div> */}
              </div>
            ):(<Navigate to="/dashboard/deepdive" replace />)}
          </div>
        </div>

        {/* Insights Panel */}
        <div className="space-y-6">
          {/* Key Metrics */}
          <div className="bg-card rounded-2xl p-6 border border-border">
            <h3 className="text-lg font-semibold text-foreground mb-4">Key Metrics</h3>
            <div className="space-y-4">
              <div>
                <div className="flex justify-between mb-1">
                  <span className="text-text-muted text-sm">Sample Size</span>
                  <span className="text-foreground font-medium">{(health.traditional.metrics.total_count || 0).toLocaleString('en-US')}</span>
                </div>
                <div className="w-full bg-background-tertiary rounded-full h-2">
                  <div
                    className="bg-gradient-to-r from-cyan-500 to-cyan-600 h-2 rounded-full"
                    style={{ width: '100%' }}
                  ></div>
                </div>
              </div>
              <div>
                <div className="flex justify-between mb-1">
                  <span className="text-text-muted text-sm">NPS</span>
                  <span className="text-foreground font-medium">{(health.traditional.metrics.nps || 0).toFixed(0)}</span>
                </div>
                <div className="w-full bg-background-tertiary rounded-full h-2">
                  <div
                    className={`h-2 rounded-full bg-gradient-to-r ${
                      (health.traditional.metrics.nps || 0) > 50 ? 'from-emerald-500 to-emerald-600' :
                      (health.traditional.metrics.nps || 0) > 0 ? 'from-amber-500 to-amber-600' :
                      'from-red-500 to-red-600'
                    }`}
                    style={{ width: `${(health.traditional.metrics.nps || 0)}%` }}
                  ></div>
                </div>
              </div>
              <div>
                <div className="flex justify-between mb-1">
                  <span className="text-text-muted text-sm">Loyalty</span>
                  <span className="text-foreground font-medium">{(health.traditional.metrics.loyalty || 0).toFixed(0)}%</span>
                </div>
                <div className="w-full bg-background-tertiary rounded-full h-2">
                  <div
                    className="bg-gradient-to-r from-primary to-primary h-2 rounded-full"
                    style={{ width: `${health.traditional.metrics.loyalty || 0}%` }}
                  ></div>
                </div>
              </div>
            </div>
          </div>

          {/* Demographic Impact - TODO: Add filtered vs unfiltered metrics */}
          {/* filteredData && (
            <div className="bg-card rounded-2xl p-6 border border-amber-500/20">
              <h3 className="text-lg font-semibold text-foreground mb-4">Filter Impact</h3>
              <p className="text-text-muted text-sm mb-3">
                Changes from overall population
              </p>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-text-muted text-sm">Awareness</span>
                  <span className={`text-sm font-medium ${
                    filteredData.awareness > metrics.awareness ? 'text-emerald-500' : 'text-red-500'
                  }`}>
                    {filteredData.awareness > metrics.awareness ? '+' : ''}
                    {((filteredData.awareness - metrics.awareness) / metrics.awareness * 100).toFixed(1)}%
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-text-muted text-sm">Consideration</span>
                  <span className={`text-sm font-medium ${
                    filteredData.consideration > metrics.consideration ? 'text-emerald-500' : 'text-red-500'
                  }`}>
                    {filteredData.consideration > metrics.consideration ? '+' : ''}
                    {((filteredData.consideration - metrics.consideration) / metrics.consideration * 100).toFixed(1)}%
                  </span>
                </div>
              </div>
            </div>
          ) */}

        </div>
      </div>

      {pageName && <AIInsightPanel title="AI Analysis" insights={pageInsights} loading={pageInsightsLoading} />}
    </div>
  );
};

export default DeepDiveDashboard;
